import React, { useEffect, useRef, useState } from "react";
import styles from "./css/PublicCourseCard.module.css";
import CourseTitle from "./CourseTitle";
import InstructorName from "./InstructorName";
import StudentCount from "./StudentCount";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Button from "../../../../src/components/assignmentAndQuiz/Button/Button";
import ReactHtmlParser from "react-html-parser";
import CourseLength from "./CourseLength";

const deterministicStudentCountFromId = (id) => {
  // returns integer in [1000, 4000] deterministically from the id string
  if (!id || typeof id !== "string") return 0;
  // djb2-like hash
  let hash = 5381;
  for (let i = 0; i < id.length; i++) {
    // keep as integer
    hash = ((hash << 5) + hash) + id.charCodeAt(i); /* hash * 33 + c */
    // keep hash in 32-bit range
    hash = hash & 0xffffffff;
  }
  // map to 0..3000 inclusive (since 1000..4000 inclusive -> size 3001)
  const rangeSize = 4000 - 1000 + 1; // 3001
  // convert to non-negative
  const positive = Math.abs(hash);
  const offset = positive % rangeSize;
  return 1000 + offset;
};

const PublicCourseCard = (props) => {
  const { t } = useTranslation();
  const history = useHistory();

  // refs for measuring title and detecting if it's multiline
  const titleRef = useRef(null);
  const containerRef = useRef(null);
  // boolean flag that indicates whether title spans more than 1 line
  const [isTitleMultiLine, setIsTitleMultiLine] = useState(false);

  // update the boolean flag when title or window size changes
  useEffect(() => {
    function updateIsMultiLine() {
      const titleEl = titleRef.current;
      if (!titleEl) return;
      const style = window.getComputedStyle(titleEl);
      const lineHeightPx = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
      const titleHeight = titleEl.getBoundingClientRect().height;
      const titleLines = Math.max(1, Math.round(titleHeight / lineHeightPx));
      setIsTitleMultiLine(titleLines > 1);
    }

    // initial check
    updateIsMultiLine();

    // update on resize (debounced)
    let resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateIsMultiLine, 120);
    }
    window.addEventListener("resize", onResize);

    // if webfonts can cause layout shift, re-check when fonts ready
    if (document?.fonts?.ready) {
      document.fonts.ready.then(updateIsMultiLine).catch(() => {});
    }

    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    };
  }, [props.title]);

  const descRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = descRef.current;
    if (!el) return;

    const checkOverflow = () => {
      // small tolerance for sub-pixel differences
      const overflowing =
        el.scrollHeight > el.clientHeight + 1 ||
        el.scrollWidth > el.clientWidth + 1;
      setIsOverflowing(overflowing);
    };

    // initial check
    checkOverflow();

    // ResizeObserver: watches the description box size/content
    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => checkOverflow());
      ro.observe(el);
    }

    // also watch window resize (debounced)
    let resizeTimer = null;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkOverflow, 120);
    };
    window.addEventListener("resize", onResize);

    // re-check after webfonts load (if used)
    if (document?.fonts?.ready) {
      document.fonts.ready.then(checkOverflow).catch(() => {});
    }

    // if description prop changes, re-check
    // (effect will re-run because of dependency below)

    return () => {
      if (ro && ro.disconnect) ro.disconnect();
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    };
  }, [props.description]);

  const count = (typeof props.studentCount === "number" && props.studentCount >= 1000)
  ? props.studentCount
  : deterministicStudentCountFromId(props?.id);

  const handleEnrollOrView = () => {
    history.push({
      pathname: `/course/view/${props?.id}`,
    });
  };

  return (
    <div
      className={styles.publicCourseCardContainer}
      ref={containerRef}
      onClick={handleEnrollOrView}
    >
      <div className={styles.imageContainer}>
        {/* Course Level Badge */}
        {props?.course_level && (
          <span className={`${styles.levelBadge} ${styles[props?.course_level.toLowerCase()]}`}>
            {t(`course.${props?.course_level}`)}
          </span>
        )}
        <img src={props?.image} alt="" className={styles.courseImage} />
      </div>
      <div className={styles.smallContanier}>
        <div className={styles.metaContanier}>
        {/* <div id={styles.courseTitle}><CourseTitle title="Title" /></div> */}
        {props?.isLive && (
          <div className={styles.liveBadgeContainer}>
            <img src="/Image/CourseMonetisation/LiveSchedule/Live-badge-cards.svg" aria-hidden="true" className={styles.liveBadge} />
          </div>
        )}

        <div
          ref={titleRef}
          className={`${styles.courseTitle} ${props?.isLive ? styles.courseTitleWithBadge : ""} ${isTitleMultiLine ? styles.courseTitleMultiLine : ""}`}
          data-title-multiline={isTitleMultiLine ? "true" : "false"}
        >
          {props?.title}
        </div>

        {/* <div id={styles.descCont}><span className={styles.description}>{ReactHtmlParser("Description")}</span></div> */}
        <div
          className={`${styles.descCont} ${isTitleMultiLine ? styles.descContMultiLine : ""}`}
          data-overflow={isOverflowing ? "true" : "false"}
        >
          <span
            ref={descRef}
            className={`${styles.description} ${isTitleMultiLine ? styles.descriptionMultiLine : ""}`}
            tabIndex={isOverflowing ? 0 : -1}
            aria-expanded={isOverflowing ? "false" : undefined}
          >
            {ReactHtmlParser(props?.description)}
          </span>

          {isOverflowing && (
            <div
              className={styles.fullDescription}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="Full description"
            >
              {ReactHtmlParser(props?.description)}
            </div>
          )}
        </div>

        {/* <div id={styles.instructor}><InstructorName instructor="Instructor" /></div> */}
        <div id={styles.instructor}>
          <div className={styles.instructor_name}>
            <img style={{ width: 16, height: 16 }} src="/Image/CourseMonetisation/user-2-line.svg" alt="" />
            <span>{props.instructor ?? ""}</span>
          </div>
        </div>
        <div className={styles.student_count}>
          <img style={{ width: 14, height: 14 }}  src="/Image/CourseMonetisation/student1.svg" alt="" />
          <span>{(count > 1) ? (count + ` ${t("courseMonetisation.students")}`) : count + ` ${t("courseMonetisation.student")}` }</span>
        </div>
        <div className={styles.courseLength}>
          <img src="/Image/lecturePlan/info.svg" alt="course length" />
          <span>
            {props.courseLength < 60
              ? `${Math.floor(props.courseLength)} total minutes`
              : `${Math.floor(props.courseLength / 60)} total hours`
            }
            &nbsp;&#x2022;&nbsp;
          </span>
          <span>
            {props.lectureCount > 1
              ? `${props.lectureCount} ${t("common.lectures")}`
              : `${props.lectureCount} ${t("common.lecture")}`}
          </span>
        </div>
        </div>
        <div className={styles.priceAndEnrollContainer}>
          {/* <span className={styles.publicCoursePrice}>FREE</span> */}
          <span className={styles.publicCoursePrice}>
            {props?.price > 0 ? (
              props?.discounted_price > 0
                ? (
                  <>
                    <span>${props?.discounted_price}</span>
                    <span style={{ textDecoration: 'line-through', fontWeight: 400, fontSize: 14, marginLeft: 6 }}>
                      ${props?.price}
                    </span>
                  </>
                )
                : `$${props?.price}`
            ) : (
              t("common.free")
            )}
          </span>
          <button
            onClick={handleEnrollOrView}
            className={styles.publicCourseButton}
          >
            {
              props?.isOwner
                ? t("common.view") // if course is of owner itself dont show enroll button show only view btn
                : props.isEnrolled
                ? t("common.view")
                : t("courseMonetisation.enroll")
            }
          </button>
          {/* <Button
            type="solid"
            label={
              props?.isOwner
                ? t("common.view") // if course is of owner itself dont show enroll button show only view btn
                : props.isEnrolled
                ? t("common.view")
                : t("courseMonetisation.enroll")
            }
            clickHandler={handleEnrollOrView}
            disable={!props.isActive && !props.isEnrolled ? true : false}
          /> */}
          {/* <div>
            <span>{props.isOwner ? "owner" : "not owner"}</span>
            <span>{props.isDrafted ? "Draft" : "Published"}</span>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default PublicCourseCard;
